import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Smartphone, Download, CheckCircle, Music, Calendar, Users } from 'lucide-react';

export const WelcomePage = ({ onDismiss }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    // Detectar si ya está en modo standalone (instalada)
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                      window.navigator.standalone ||
                      document.referrer.includes('android-app://');
    
    setIsStandalone(standalone);

    // Detectar iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // Capturar el evento de instalación
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Si no hay prompt (iOS o ya instalado), mostrar instrucciones
      setShowInstructions(true);
      return;
    }

    // Mostrar prompt de instalación
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('PWA instalada');
    }
    
    setDeferredPrompt(null);
  };

  // Si ya está instalada, marcar como visto y continuar
  if (isStandalone && onDismiss) {
    onDismiss();
    return null;
  }

  const handleContinue = () => {
    if (onDismiss) {
      onDismiss();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full space-y-6">
        {/* Logo y título */}
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <img 
              src="/logo.png" 
              alt="Luis Herrera Eventos" 
              className="h-24 w-auto"
            />
          </div>
          <h1 className="text-4xl font-bold text-white">
            Luis Herrera Eventos
          </h1>
          <p className="text-xl text-slate-300">
            DJ TEAM - Gestor de Eventos
          </p>
        </div>

        {/* Card principal */}
        <Card className="bg-slate-800/50 border-slate-700 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white text-center text-2xl">
              Bienvenido 🎉
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-slate-300 text-center">
              Esta es una aplicación web progresiva (PWA). Para la mejor experiencia, instálala en tu dispositivo.
            </p>

            {/* Características */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex flex-col items-center text-center space-y-2">
                <Music className="w-8 h-8 text-cyan-400" />
                <p className="text-slate-300 text-sm">Biblioteca Musical</p>
              </div>
              <div className="flex flex-col items-center text-center space-y-2">
                <Calendar className="w-8 h-8 text-pink-400" />
                <p className="text-slate-300 text-sm">Gestión de Eventos</p>
              </div>
              <div className="flex flex-col items-center text-center space-y-2">
                <Users className="w-8 h-8 text-purple-400" />
                <p className="text-slate-300 text-sm">Trabajo en Equipo</p>
              </div>
            </div>

            {/* Botón de instalación */}
            {!showInstructions ? (
              <div className="space-y-3">
                <Button
                  onClick={handleInstallClick}
                  className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-6 text-lg"
                  size="lg"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Instalar Aplicación
                </Button>
                
                <Button
                  onClick={handleContinue}
                  variant="outline"
                  className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  Continuar sin instalar
                </Button>
              </div>
            ) : (
              // Instrucciones de instalación (especialmente para iOS)
              <Card className="bg-slate-900/50 border-slate-600">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-cyan-400" />
                    Instrucciones de Instalación
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isIOS ? (
                    // Instrucciones iOS
                    <div className="space-y-3 text-slate-300">
                      <p className="font-semibold text-white">En Safari (iPhone/iPad):</p>
                      <ol className="list-decimal list-inside space-y-2 text-sm">
                        <li>Toca el botón <strong>Compartir</strong> (cuadrado con flecha ↑)</li>
                        <li>Desplázate hacia abajo</li>
                        <li>Selecciona <strong>"Añadir a pantalla de inicio"</strong></li>
                        <li>Toca <strong>"Añadir"</strong></li>
                      </ol>
                      <div className="flex items-start gap-2 p-3 bg-cyan-900/20 border border-cyan-600/30 rounded">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm">
                          La app aparecerá en tu pantalla de inicio como cualquier otra aplicación
                        </p>
                      </div>
                    </div>
                  ) : (
                    // Instrucciones Android/Chrome y Safari Mac
                    <div className="space-y-4 text-slate-300">
                      <div>
                        <p className="font-semibold text-white mb-2">En Chrome (Android):</p>
                        <ol className="list-decimal list-inside space-y-2 text-sm">
                          <li>Toca el menú <strong>⋮</strong> (tres puntos)</li>
                          <li>Selecciona <strong>"Instalar aplicación"</strong></li>
                          <li>Confirma la instalación</li>
                        </ol>
                      </div>
                      
                      <div className="border-t border-slate-600 pt-3">
                        <p className="font-semibold text-white mb-2">En Safari (Mac):</p>
                        <ol className="list-decimal list-inside space-y-2 text-sm">
                          <li>Click en <strong>Archivo</strong> en el menú superior</li>
                          <li>Selecciona <strong>"Añadir a Dock"</strong></li>
                          <li>La app aparecerá en tu Dock de macOS</li>
                        </ol>
                      </div>
                      
                      <div className="flex items-start gap-2 p-3 bg-cyan-900/20 border border-cyan-600/30 rounded">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm">
                          También puedes continuar sin instalar desde el navegador
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <Button
                    onClick={handleContinue}
                    className="w-full bg-slate-700 hover:bg-slate-600 text-white mt-4"
                  >
                    Entendido, continuar
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Nota sobre acceso */}
            <div className="text-center text-sm text-slate-400 space-y-1">
              <p>💡 <strong>Nota:</strong> Esta aplicación requiere inicio de sesión</p>
              <p>Si no tienes cuenta, contacta con el administrador</p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-slate-500 text-sm">
          <p>© 2025 Luis Herrera Eventos - DJ TEAM</p>
        </div>
      </div>
    </div>
  );
};
